'use client';
import { StockSymbol } from '@benzinga/session';
import styled from '@benzinga/themetron';

import Hooks from '@benzinga/hooks';
import { GridApi } from 'ag-grid-community';

import React, { useCallback, useState } from 'react';
import Ticker from './Ticker/Ticker';
import { TickerColor } from '../TickerColor';
import { TickerPopover } from './TickerPopover';
import { PopoverState, PositionTransformValue } from 'react-tiny-popover';

interface Props {
  gridApi: GridApi;
  onSymbolClick: (symbol: StockSymbol) => void;
  rowIndex: number;
  symbol: StockSymbol;
}

export const TickerTooltipCellRenderer = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const [renderTooltip, setRenderTooltip] = useState(false);
  const [closeOnClick, setCloseOnClick] = React.useState<boolean>(false);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);

  const showTooltip = useCallback(() => {
    setRenderTooltip(true);
  }, []);

  const hideTooltip = useCallback(() => {
    if (!closeOnClick) {
      setRenderTooltip(false);
    }
  }, [closeOnClick]);

  Hooks.useClickOutside(tooltipRef, () => {
    setRenderTooltip(false);
    setCloseOnClick(false);
  });

  const renderHover = React.useCallback(() => {
    return (
      <div>
        {closeOnClick && <TooltipOverlay />}
        <Ticker
          isOnlySymbol
          key={props.symbol}
          modules={{
            chart: {
              referenceTime: new Date().getTime().toString(),
            },
          }}
          onDialogModeChange={setCloseOnClick}
          onSymbolClick={props.onSymbolClick}
          symbol={props.symbol}
        />
      </div>
    );
  }, [closeOnClick, props.onSymbolClick, props.symbol]);

  const transform = React.useCallback<(popoverState: PopoverState) => PositionTransformValue>(popoverState => {
    const childRect = popoverState.childRect;
    const popoverRect = popoverState.popoverRect;
    const windowRect = window.document.body.getBoundingClientRect();

    const x = (() => {
      if (childRect.right + popoverRect.width > windowRect.width) {
        //if right side blocked
        if (childRect.left - popoverRect.width < 0) {
          //if left side blocked
          return windowRect.width - popoverRect.width;
        }
        return childRect.left - popoverRect.width;
      }
      return childRect.right;
    })();
    const y = (() => {
      if (childRect.top + popoverRect.height > windowRect.height) {
        //if bottom blocked
        if (childRect.top - popoverRect.height < 0) {
          //if top blocked
          return windowRect.height - popoverRect.height;
        }
        return childRect.top - popoverRect.height;
      }
      return childRect.top;
    })();

    return { left: x, top: y };
  }, []);

  return (
    <Div onMouseEnter={showTooltip} onMouseLeave={hideTooltip} ref={ref}>
      <TickerPopover content={renderHover()} transform={transform} transformMode="absolute" visible={renderTooltip}>
        <Div2>
          <TickerColor onSymbolClick={props.onSymbolClick} symbol={props.symbol} />
        </Div2>
      </TickerPopover>
    </Div>
  );
});
const Div = styled.div`
  width: fit-content;
`;
const Div2 = styled.div`
  padding-right: 5px;
`;

const TooltipOverlay = styled.div`
  position: absolute;
  top: -2000px;
  height: 4000px;
  width: 300px;
  z-index: 10000;
`;
